/**
 * Uplifted pieces of `utils/constants` (apps/web + apps/mobile).
 *
 * This folder MIRRORS the app file and holds only what has actually been needed so far —
 * `BRANCH_OPTIONS` (for `config/endpoints`'s divergent `OUTLETSTATUS.toggleBranch`) and the
 * two cash-register constants `CashRegisterSlice` reads. Add the next required symbol HERE;
 * the apps' `utils/constants` re-export from this folder, so each addition is one more name
 * on that re-export line.
 *
 * ⚠️ `CASH_REGISTER_MODAL_TYPES` is NOT here — it DIVERGES (web has a fourth key,
 * `close_switch_user`), so it lives in `index.desktop.ts` / `index.native.ts`. Any core file
 * reading it must deep-import the matching variant (ledger hazard 16).
 *
 * The app files still own everything else (~100 constants). `utils/constants` is also the
 * listed blocker for PrinterSlice, which will extend this folder again.
 */

/** Identical in both apps. Read by `OUTLETSTATUS.toggleBranch` on both platforms. */
export const BRANCH_OPTIONS = {
  close: 'close',
  open: 'open',
  inactive: 'Inactive',
  active: 'Active',
};

/**
 * Identical in both apps (web spells the values with double quotes, mobile single — same
 * strings). Read by `CashRegisterSlice.saveCashSettlementDetails` on both platforms.
 */
export const CASH_SETTLEMENT_STATUS_TYPES = {
  draft: 'Draft',
  open: 'Open',
  close: 'Close',
};

/**
 * Item-tag names, identical in both apps (web spells EGG with double quotes, mobile single —
 * same string). Read by `catalogueHelpers.setPriorityForItemTags` on mobile.
 */
export const EGG = 'egg';
export const NON_VEGETARIAN = "non-vegetarian";
export const VEGETARIAN = "vegetarian";
export const VEG = "veg";
export const NVEG = "nveg";
export const NVEG_ICON = "nveg-food-icn";
export const VEG_ICON = "veg-food-icn";
export const EGG_ICON = "egg-food-icn";

/**
 * Network status marker written to `LocalServerSlice.networkStatus` by both apps' response
 * interceptors on a 200. Identical string in both apps (`"online"`); the offline counterpart is
 * an inline `"offline"` literal on both sides and is deliberately NOT lifted here — it has no
 * named constant to mirror.
 */
export const ONLINE = "online";

/**
 * Sale lifecycle states. Byte-identical in both apps (modulo quote style), so shared.
 * Uplifted for `helpers/adhocTableHelpers`, which compares against CLOSED/CANCELLED.
 */
export const SALE_STATUS_TYPES = {
  OPEN: "Open",
  CLOSED: "Closed",
  VOIDED: "Voided",
  CANCELLED: "Cancelled",
  HELD: "Held",
  AMOUNT_DUE: "Amount Due",
  ADVANCE_ORDER: "Advance Order",
};

/**
 * redux-persist's storage key for the persisted root. Byte-identical in both apps
 * (`"persist:root"`, and it must be — it is the key already written on every installed
 * device, so it is effectively frozen). Read by mobile's `redux-store/migration` when it
 * writes the migrated blob, and by both apps' own state helpers.
 */
export const REDUX_PERSIST_STATE = "persist:root";

/**
 * The localStorage/MMKV key under which this terminal's device identifier is stored.
 * Byte-identical in both apps (`"deviceIdentifier"`), so shared.
 *
 * ⚠️ Note the SAME string is also reachable as `LOCALSTORAGE_KEYS.deviceIdentifier` — mobile's
 * `helper/localServer.prepareLsPayloadData` reads it that way while web reads it through this
 * constant. Both resolve to `"deviceIdentifier"`, which is why `prepareLsPayloadData` could be
 * shared rather than split; the probe asserts the resolved key, not the constant name.
 *
 * Uplifted for `helpers/localServer` (`isMaster`, `prepareLsPayloadData`).
 */
export const DEVICEIDENTIFIER = "deviceIdentifier";

/**
 * Sale sync states, written to `sale.syncStatus` by the offline layer and read back by the
 * sync sweeps. Byte-identical in both apps (modulo quote style), so shared.
 *
 * Uplifted for `helpers/OfflineHelper` (`updateSyncedSale`, `replaceLocalSale`).
 */
export const SALE_SYNC_STATUSES = {
  SYNCED: "synced",
  TO_BE_SYNCED: "tobesynced",
  CONFLICTED: "conflicted",
  ERRORED: "errored",
};

/**
 * How a sale reached the local store. Byte-identical in both apps (modulo quote style).
 *
 * Uplifted for `helpers/OfflineHelper.replaceLocalSale`, which repairs a sale whose
 * `operation` is `SAVE_SALE` but whose `syncStatus` says it is already synced.
 */
export const SALE_PROCESS_OPERATION_TYPES = {
  FORCE_SAVE_SALE: "FORCE_SAVE_SALE",
  SAVE_SALE: "SAVE_SALE",
};

/**
 * The object key both apps map catalogue collections by. Byte-identical (`"mid"` on one side,
 * `'mid'` on the other — quote style, not a difference).
 *
 * Uplifted for `saga/pos/catalog`, which passes it to `arrayToMap`. It is a KEY NAME, not an id:
 * every catalogue entity carries a `mid` field and every `arrayToMap(list, MID)` call turns a
 * list into a map keyed by it.
 */
export const MID = "mid";

/**
 * A kitchen ticket's print status. Byte-identical in both apps (modulo quote style).
 *
 * Uplifted for `helpers/OfflineHelper.updateAllTicketsPrintStatus`, which `@rista/core/printing`'s
 * status sink calls offline: a Completed ticket is also marked `printed`.
 */
export const TICKET_PRINT_STATUS_TYPES = {
  COMPLETED: "Completed",
  PENDING: "Pending",
  FAILED: "Failed",
};

/** The success toast shown after an order closes. Byte-identical in both apps (note the typo
 * "succesfully", which is the apps' own and is preserved — it is user-visible copy, so fixing it
 * is a product change, not an uplift). */
export const ORDER_CLOSED = "Order closed succesfully!";

/** Backend error code. Byte-identical in both apps. */
export const REST_SALE_ITEMS_ON_HOLD = "REST_SALE_ITEMS_ON_HOLD";

/**
 * The three states a discount approval can be in.
 *
 * ⚠️ **The two apps declare this in DIFFERENT source files** — web in `utils/constants`, mobile
 * in `data/constants` — but with byte-identical values (diffed, not assumed; mobile adds
 * `as const`, which is a type annotation, not a value). It lives here, mirroring web's source,
 * and BOTH app files re-export it so neither set of importers changed.
 *
 * ⚠️ `as const` is deliberately NOT carried over: it would narrow the values to string literals
 * for web too, and nothing on either platform uses this as a TYPE (checked with a type-position
 * grep before collapsing).
 */
export const DISCOUNT_APPROVAL_STAGE = {
  APPROVED: 'Approved',
  REQUESTED: 'Requested',
  REJECTED: 'Rejected'
}

/**
 * Millisecond durations. **Byte-identical in both apps, comments included** (diffed, not assumed).
 * Uplifted for a `helpers/catalogueHelpers` staleness check that compares against the day value.
 *
 * ⚠️ Each is derived from the one above it, so they must stay together and in this order.
 */
export const MILI_SECS_IN_ONE_SEC = 1000;
export const MILI_SECS_IN_ONE_MIN = 60 * MILI_SECS_IN_ONE_SEC; // 60 * 1000
export const MILI_SECS_IN_ONE_HOUR = 60 * MILI_SECS_IN_ONE_MIN; // 60 * 60 * 1000
export const MILI_SECS_IN_ONE_DAY = 24 * MILI_SECS_IN_ONE_HOUR; // 24 * 60 * 60 * 1000

/**
 * How long a sale may sit in the local store before the startup sweep removes it.
 * **Byte-identical in both apps**, comment included.
 *
 * ⚠️ Both entries are the same 5 days. The two keys exist so open and closed orders CAN diverge,
 * not because they do — do not collapse them to one constant.
 */
export const ORDERS_CLEANUP_INTERVALS = {
  openOrder: 5 * MILI_SECS_IN_ONE_DAY,
  otherOrder: 5 * MILI_SECS_IN_ONE_DAY,
};

/**
 * The Qwikcilver gift-card payment mode, as it appears in `paymentInfo.payments[].mode`.
 *
 * Identical on both platforms (`export const QWIKCILVER = "qwikcilver";` in each app's
 * `utils/constants`), so it lives here rather than in a variant.
 */
export const QWIKCILVER = "qwikcilver";

/**
 * `opType` passed to the rista-library ticket-creation check when a sale is SAVED (as opposed to
 * closed, which passes the literal `"Close"`).
 *
 * Identical in both apps' `utils/constants` (`"Save"`), so it lives here. Note the apps also pass
 * the bare literals `"Save"` / `"Close"` at some call sites — that inconsistency is theirs and is
 * transcribed as-is in `helpers/OfflineHelper`.
 */
export const CREATE_TICKET_ON_SAVE = "Save";

/**
 * Toast text shown when a cancellation refunded the customer's payment.
 *
 * 🔴 **CROSS-FILE**: identical string on both platforms, but the apps keep it in different files —
 * web in `utils/constants`, mobile in `components/ExternalPaymentProvider/constants`. It lives
 * here, in the shared half, because the VALUE is the same and one definition beats mirroring an
 * accident of placement; both app files re-export it.
 */
export const PAYMENT_SUCCESSFULLY_REFUNDED = "Payment successfully refunded";

/**
 * Pulled up by the `MyOrderCard` dependency closure — `getOrderFulfillmentStatus` and friends
 * (see `helpers/myOrderHelpers/shared.ts`) need both, and neither was in core.
 *
 * ⚠️ WEB'S VERSION IS A SUPERSET, and that is what is here. The first nine keys are identical
 * in both apps; web additionally carries `RETURN_INITIATED` / `RETURN_COMPLETED` /
 * `RETURN_FAILED`. Adopting the superset is purely additive for mobile — three keys nothing on
 * that platform reads yet — so no behaviour moves. Verified by diffing the two app files, not
 * assumed.
 */
export const FULFILLMENT_STATUS_TYPES = {
  CREATED: "Created",
  ACCEPTED: "Accepted",
  PROCESSED: "Processed",
  DISPATCHED: "Dispatched",
  DELIVERED: "Delivered",
  CONFIRMED: "Confirmed",
  CANCELLED: "Cancelled",
  OVERVIEW: "Overview",
  ADVANCE_ORDER: "Advance Order",
  RETURN_INITIATED: "Return_Initiated",
  RETURN_COMPLETED: "Return_Completed",
  RETURN_FAILED: "Return_Failed",
};

/**
 * Uplifted because `helper/myOrderHelpers` imports it. Same value in every copy.
 *
 * ⚠️ Both apps declare `PICKUP` a SECOND time, in `data/constants` — a duplicate that predates
 * this uplift. Those two are re-exports of this constant now as well, and had to be: the moment
 * core declares the name, an app declaring it too is a `pnpm audit:uplift` check A hit
 * ("core declares X, an app also declares X"), which is the drift core exists to end. So this
 * is four app-side re-export lines, not two. The `SELECTION_MODES.PICKUP` object KEY in the
 * same two files is untouched — a property name, not a binding.
 */
export const PICKUP = "Pickup";


/**
 * Uplifted by the `MyOrderCard` intent closure. `DINE_IN` is declared identically in both apps'
 * `utils/constants` ("Dine In"); `ADVANCE_ORDER` exists only in web's `utils/myOrderConstants`
 * and has NO mobile counterpart at all, so it arrives there as new — additive, nothing moves
 * until mobile's `getSendData` starts matching on it (which web's converged body does).
 */
export const DINE_IN = "Dine In";
export const ADVANCE_ORDER = "advance-order";

/**
 * The CTA vocabulary `myOrders/orderCard.getCtaInfo` reads. All three were CROSS-LOCATED
 * between the apps, which is why they sit together here instead of each mirroring one file:
 *
 *   ACTION_STATES          web `data/constants`  | mobile `utils/constants`
 *   CTA_DISABLE_STATUSES   web `utils/constants` | mobile `data/constants`
 *   REVERSE_LOGISTIC_FLOW  web `utils/constants` | mobile — absent
 *
 * `ACTION_STATES` is WEB's SUPERSET: mobile lacked `RETURN_INITIATED`, which is additive there —
 * nothing on that platform matched on it until `getCtaInfo` did, and that is a gain (the
 * return-flow CTA states web shows). `CTA_DISABLE_STATUSES` was identical in both apps and lives
 * only here.
 */
export const ACTION_STATES = {
  REMIND: "Remind",
  IN_PROCESS: "In Process",
  CANCELLED: "Cancelled",
  VOIDED: "Voided",
  RETURN_INITIATED: "RETURN INITIATED",
};

export const REVERSE_LOGISTIC_FLOW = {
  RETURN_INITIATED: "RETURN INITIATED",
  RETURN_COMPLETED: "RETURN COMPLETED",
  RETURN_FAILED: "RETURN FAILED",
};

/**
 * Statuses for which the order card's action CTA is not pressable: titles that are a status label,
 * not a button. "Dispatched" is one (release_4.0.0, DP-8153, ported at the merge of 2026-10-05): a
 * POS user drives an order only as far as Dispatch, where a pickup order is handed over, and the
 * legacy POS showed a plain "Dispatched"; a delivery order reads "Pending Delivery" instead, so it
 * keeps its mark-delivered click. Read by web v1's `MyOrderCart` and My Orders v2's `orderCard`.
 */
export const CTA_DISABLE_STATUSES = ["Delivered", "Cancelled", "Voided", "Dispatched"];

/**
 * The my-orders ACTION vocabulary `myOrders/useOrderActions` reads (2026-09-15). Every string
 * below was declared in BOTH apps' `utils/constants` with the same value; `NEXT_ACTION_TYPES` is
 * web's superset (mobile lacked `ACCEPT`) and the two `OUTLIER_*` lists were web-only — mobile
 * had no outlier tip at all, and gains one with the shared card (plan §3).
 */
export const CLOSED = "closed";
export const ZOMATO = "zomato";
export const SWIGGY = "swiggy";
// moved from web's utils/constants.js 2026-09-16 (my-orders v2 reject flow); mobile had neither
export const OWNLY = "ownly";
/** the aggregator's reject reasons — the first is the out-of-stock path, the rest ask for a next-available time */
export const REJECT_REASON_DATA = [
  { title: "Item out of stock", value: "ITEM_OUT_OF_STOCK" },
  { title: "Restaurant is closed", value: "RESTAURANT_CLOSED" },
  { title: "Kitchen is busy", value: "KITCHEN_STRESS" },
  { title: "Equipment / Power issue", value: "MERCHANT_DEVICE_ISSUE" },
  { title: "No delivery partner available", value: "NO_DELIVERY_PARTNER_AVAILABLE" },
  { title: "Customer outside delivery radius", value: "CUSTOMER_OUTSIDE_DELIVERY_RADIUS" },
];
/** the aggregator-data action that returns a source's help-centre link (More menu → Help Center) — moved from both apps 2026-09-17 */
export const GET_HELP_URL = "GET_HELP_URL";
/** the API's "no such sale" code — the barcode search's "Bill number … is not found" — identical in both apps, moved 2026-09-17 */
export const REST_INVALID_SALE = "REST_INVALID_SALE";
/** the sale field an accept carries the chosen prep time in — identical in both apps, moved 2026-09-17 (my-orders v2 detail) */
export const PREPARATION_TIME = "preparationTime";
// TICKET_PRINT_STATUS_TYPES (the my-orders KOT view's, 2026-09-17) is defined once above — release_4.0.0
// uplifted the same constant; the 2026-09-24 merge kept that copy.
export const DISPATCHED = "Dispatched";
export const PROCESS = "Process";
export const MARKREADY = "Mark Ready";
export const CREATED = "Created";
export const ACCEPTED = "Accepted";
export const CONFIRMED = "Confirmed";
export const CREATE_TICKETS = "createTickets";
export const ACCEPT = "Accept";

export const NEXT_ACTION_TYPES = {
  PROCESS: "Process",
  DISPATCH: "Dispatch",
  DELIVER: "Deliver",
  ACCEPT: "Accept",
};

export const OUTLIER_COMPLAINS = {
  QUALITY_ISSUES: "QUALITY_ISSUES",
  MISSING_ITEMS: "MISSING_ITEMS",
  WRONG_ITEMS: "WRONG_ITEMS",
  SPILLAGE_ISSUES: "SPILLAGE_ISSUES",
  PACKAGING_AND_SPILLAGE_ISSUES: "PACKAGING_AND_SPILLAGE_ISSUES",
  PACKING_ISSUES: "PACKING_ISSUES",
};

export const OUTLIER_COMPLAINS_FOR_MARKREADY = [
  "MISSING_ITEMS",
  "WRONG_ITEMS",
  "SPILLAGE_ISSUES",
  "PACKAGING_AND_SPILLAGE_ISSUES",
  "PACKING_ISSUES",
];

/** The order-card vocabulary (2026-09-15, `myOrders/orderTags` + `helpers/myOrderHelpers/orderId`). Identical in both apps. */
export const POS = "pos";
export const COD = "cod";

/** The outlier tip's quality-issue copy (2026-09-15, `sheets/OutlierTip`). Web-only in v1; mobile had no tip. */
export const QUALITY_ISSUE_MESSAGE =
  "Quality issues with some of the items of the order has been reported previously. Please ensure that none of the items are burnt, stale, oily, cold, bland or uncooked";

/** The order-detail vocabulary (2026-09-15, `myOrders/orderDetail`). Identical in both apps. */
export const OPEN = "open";
export const CANCELLED = "Cancelled";
export const API = "api";
export const SUPPORT = "support";
/** the two online sources the POS can edit / check out itself */
export const API_SUPPORT_SOURCES = [API, SUPPORT];
/** `checkCreateTicket` op types for the detail's print and close (2026-09-15). Identical in both apps. */
export const CREATE_TICKET_ON_PRINT = "Print";
export const CREATE_TICKET_ON_CLOSE = "Close";
/** Rider row + verified-items vocabulary (2026-09-15). Identical in both apps. */
export const DELIVERED = "Delivered";
export const PROCESSED = "Processed";
export const VOIDED = "Voided";
/**
 * Read by the feature gate (`helpers/featureRestrictionHelper`) and the payment-mode filter.
 * `ONLINE_CHANNEL` and `EXTERNAL` are identical in both apps; `SALE_RETURN_TYPE` and
 * `SWIGGY_DINEIN` / `DISTRICT_POSTPAID` were web-only.
 */
export const ONLINE_CHANNEL = "Online";
export const SALE_RETURN_TYPE = "return";
export const SWIGGY_DINEIN = "swiggydinein";
export const DISTRICT_POSTPAID = "districtpostpaid";
export const EXTERNAL = "External";
/** The detail's discount-approval cell: its label before a request, and after one. Identical in both apps. */
export const REQUEST_FOR_APPROVAL = "Request For Approval";
export const CHECK_STATUS = "Check Status";

const CDN_ORACLE_URL = "https://cdn.dotpe.in/oracle";

/** Shown in place of an item's own image when it has none. */
export const DEFAULT_ITEM_IMAGE_URL = `${CDN_ORACLE_URL}/cutlery.svg`;

export const TAG_TYPE_ICON_URL: Record<string, string> = {
  veg: `${CDN_ORACLE_URL}/veg.svg`,
  nveg: `${CDN_ORACLE_URL}/nveg.svg`,
  egg: `${CDN_ORACLE_URL}/egg.svg`,
};
export const ADD_NEW_TABLE_COPY = {
  adhocTitle: 'Add Adhoc Table',
  tableName: 'Table Name',
  guestCount: 'Guest Count',
  serverName: 'Server Name',
  serverPlaceholder: 'Enter Server Name',
  infoIcon: '!',
  infoText:
    'This table will auto-delete once the order is closed or after 10 minutes of inactivity.',
};
